import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import {
  Mic, Music, Radio, Video, PenTool, Rocket, DollarSign,
  Users, Sparkles, Camera, ArrowRight, ArrowLeft, Check, X,
  User, Hash, ChevronRight
} from 'lucide-react';

const ROLES = [
  { id: 'artist', label: 'Artist', icon: Mic, desc: 'Rapper, singer, musician' },
  { id: 'producer', label: 'Producer', icon: Music, desc: 'Beats, instrumentals' },
  { id: 'podcaster', label: 'Podcaster', icon: Radio, desc: 'Talk shows, interviews' },
  { id: 'streamer', label: 'Streamer', icon: Video, desc: 'Live content, gaming' },
  { id: 'content_creator', label: 'Content Creator', icon: PenTool, desc: 'Videos, photos, vlogs, skits' },
];

const GOALS = [
  { id: 'viral', label: 'Go Viral', icon: Rocket, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
  { id: 'money', label: 'Make Money', icon: DollarSign, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  { id: 'fanbase', label: 'Build Fanbase', icon: Users, color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
  { id: 'collab', label: 'Collaborate', icon: Sparkles, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' },
];

const GENRES = [
  'Hip-Hop', 'R&B', 'Pop', 'Trap', 'Drill', 'Go-Go', 'Gospel',
  'Jazz', 'Soul', 'Electronic', 'Rock', 'Afrobeats', 'Reggae', 'Podcast',
];

const FIRST_WINS = [
  { id: 'first_post', label: 'Create your first post', route: '/feed' },
  { id: 'follow_3', label: 'Follow 3 creators', route: '/discover' },
  { id: 'first_badge', label: 'Earn your first badge', route: '/leaderboard' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [step, setStep] = useState(0); // 0=role, 1=goal, 2=setup, 3=action, 4=wins
  const [role, setRole] = useState('');
  const [goals, setGoals] = useState([]);
  const [username, setUsername] = useState(user?.handle || '');
  const [genres, setGenres] = useState([]);
  const [saving, setSaving] = useState(false);

  const totalSteps = 5;
  const progress = ((step + 1) / totalSteps) * 100;

  const toggleGoal = (id) => {
    setGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const toggleGenre = (g) => {
    setGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : prev.length >= 5 ? prev : [...prev, g]);
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/profile/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          role, goals, genres, username,
          onboarding_completed: true,
        }),
      });
    } catch {} finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step === 2) await savePreferences();
    if (step < 4) setStep(step + 1);
    else navigate('/feed');
  };

  const handleSkip = () => {
    navigate('/feed');
  };

  const getActionRoute = () => {
    if (goals.includes('viral')) return '/viral-engine';
    if (goals.includes('money')) return '/creator-wallet';
    if (goals.includes('collab')) return '/discover';
    return '/feed';
  };

  return (
    <div className="fixed inset-0 bg-background z-50 overflow-y-auto" data-testid="onboarding-page">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-10">
        <div className="h-1 bg-muted/30">
          <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Skip */}
      <button
        onClick={handleSkip}
        className="fixed top-4 right-4 z-10 text-sm text-muted-foreground/50 hover:text-muted-foreground/80 transition-colors"
        data-testid="onboarding-skip"
      >
        Skip
      </button>

      <div className="max-w-md mx-auto px-4 pt-16 pb-20 min-h-screen flex flex-col">
        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8">
          {[...Array(totalSteps)].map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-violet-500' : 'bg-muted/50'}`} />
          ))}
        </div>

        <div className="flex-1">
          {/* ── Step 0: Role Selection ── */}
          {step === 0 && (
            <div className="space-y-6" data-testid="step-role">
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>What describes you best?</h1>
                <p className="text-sm text-muted-foreground/60 mt-1">We'll customize your experience</p>
              </div>
              <div className="space-y-3">
                {ROLES.map(r => (
                  <button
                    key={r.id}
                    onClick={() => setRole(r.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                      role === r.id ? 'border-violet-500 bg-violet-500/10' : 'border-border hover:border-border bg-muted/20'
                    }`}
                    data-testid={`role-${r.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-muted/30 flex items-center justify-center">
                      <r.icon className={`w-5 h-5 ${role === r.id ? 'text-violet-400' : 'text-muted-foreground/60'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{r.label}</p>
                      <p className="text-xs text-muted-foreground/50">{r.desc}</p>
                    </div>
                    {role === r.id && <Check className="w-5 h-5 text-violet-400 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 1: Goal Selection ── */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-goals">
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>What's your main goal?</h1>
                <p className="text-sm text-muted-foreground/60 mt-1">Select all that apply</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {GOALS.map(g => (
                  <button
                    key={g.id}
                    onClick={() => toggleGoal(g.id)}
                    className={`flex flex-col items-center gap-2 p-5 rounded-xl border transition-all ${
                      goals.includes(g.id) ? `${g.color}` : 'border-border hover:border-border bg-muted/20'
                    }`}
                    data-testid={`goal-${g.id}`}
                  >
                    <g.icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{g.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Quick Setup ── */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-setup">
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>Set up your profile</h1>
                <p className="text-sm text-muted-foreground/60 mt-1">Quick setup to get started</p>
              </div>

              {/* Username */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Username</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50">@</span>
                  <Input
                    value={username}
                    onChange={e => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
                    className="pl-8"
                    placeholder="yourusername"
                    data-testid="setup-username"
                  />
                </div>
              </div>

              {/* Genre tags */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Select your genres <span className="text-muted-foreground/50">(up to 5)</span></label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map(g => (
                    <button
                      key={g}
                      onClick={() => toggleGenre(g)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        genres.includes(g) ? 'bg-violet-500/20 border-violet-500/40 text-violet-300' : 'border-border text-muted-foreground/60 hover:border-border'
                      }`}
                      data-testid={`genre-${g.toLowerCase()}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Action Trigger ── */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-action">
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>Let's get started</h1>
                <p className="text-sm text-muted-foreground/60 mt-1">Based on your goals, here's your best first move</p>
              </div>

              <Card className="p-6 bg-violet-500/5 border-violet-500/20 text-center">
                <Rocket className="w-10 h-10 text-violet-400 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-1">
                  {goals.includes('viral') && 'Check Your Viral Score'}
                  {goals.includes('money') && !goals.includes('viral') && 'Set Up Monetization'}
                  {goals.includes('collab') && !goals.includes('viral') && !goals.includes('money') && 'Discover Collaborators'}
                  {!goals.includes('viral') && !goals.includes('money') && !goals.includes('collab') && 'Explore the Feed'}
                </h3>
                <p className="text-sm text-muted-foreground/60 mb-4">Your personalized starting point</p>
                <Button
                  className="rounded-full gap-2"
                  onClick={() => navigate(getActionRoute())}
                  data-testid="action-trigger-btn"
                >
                  Go Now <ArrowRight className="w-4 h-4" />
                </Button>
              </Card>

              <p className="text-xs text-center text-muted-foreground/50">Or continue to complete your first wins below</p>
            </div>
          )}

          {/* ── Step 4: First Win System ── */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-wins">
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, system-ui' }}>Your First Wins</h1>
                <p className="text-sm text-muted-foreground/60 mt-1">Complete these to earn XP and badges</p>
              </div>

              <div className="space-y-3">
                {FIRST_WINS.map(w => (
                  <button
                    key={w.id}
                    onClick={() => navigate(w.route)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-border bg-muted/20 hover:border-border transition-all text-left group"
                    data-testid={`win-${w.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center border border-border">
                      <Check className="w-4 h-4 text-muted-foreground/40" />
                    </div>
                    <span className="text-sm font-medium flex-1">{w.label}</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground/70 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>

              <Card className="p-4 bg-amber-500/5 border-amber-500/20 text-center">
                <p className="text-sm text-amber-400 font-medium">Complete all 3 to earn the "Early Bird" badge!</p>
              </Card>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep(step - 1)} className="gap-1" data-testid="onboarding-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          ) : <div />}

          <Button
            onClick={handleNext}
            disabled={
              (step === 0 && !role) ||
              (step === 1 && goals.length === 0) ||
              saving
            }
            className="rounded-full gap-2 bg-violet-600 hover:bg-violet-700 px-6"
            data-testid="onboarding-next"
          >
            {step === 4 ? 'Get Started' : 'Continue'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
